import { useEffect, useState } from 'preact/hooks';
import './AssistantWelcomeMessage.less';
import {
    hasBeenViewed,
    markAsViewed
} from '../../services/WelcomeMessageService';

export type AssistantWelcomeMessageProps = {
    mainTitle: string;
    secondaryTitle: string;
    onFinished: () => void;
};

const AssistantWelcomeMessage = ({
    mainTitle,
    secondaryTitle,
    onFinished
}: AssistantWelcomeMessageProps) => {
    const [mainTitleHighlighted, setMainTitleHighlighted] = useState('');
    const [secondaryTitleHighlighted, setSecondaryTitleHighlighted] =
        useState('');
    const [isFinishing, setFinishing] = useState(false);
    const [isFinished, setFinished] = useState(hasBeenViewed());
    useEffect(() => {
        if (isFinished) {
            onFinished();
            return;
        }
        markAsViewed();
        let intermediateTimeout: NodeJS.Timeout;
        let finishingTimeout: NodeJS.Timeout;
        let secondaryInterval: NodeJS.Timeout;
        let mainInterval: NodeJS.Timeout;
        let mainTitleHighlighted = '';
        let secondaryTitleHighlighted = '';
        const startTimeout = setTimeout(() => {
            mainInterval = setInterval(() => {
                if (mainTitle.length <= mainTitleHighlighted.length) {
                    clearInterval(mainInterval);
                    intermediateTimeout = setTimeout(() => {
                        secondaryInterval = setInterval(() => {
                            if (
                                secondaryTitle.length <=
                                secondaryTitleHighlighted.length
                            ) {
                                clearInterval(secondaryInterval);
                                setFinishing(true);
                                finishingTimeout = setTimeout(() => {
                                    setFinished(true);
                                    onFinished();
                                }, 2500);
                                return;
                            }
                            secondaryTitleHighlighted = secondaryTitle.slice(
                                0,
                                secondaryTitleHighlighted.length + 1
                            );
                            setSecondaryTitleHighlighted(
                                secondaryTitleHighlighted
                            );
                        }, 25);
                    }, 700);
                    return;
                }
                mainTitleHighlighted = mainTitle.slice(
                    0,
                    mainTitleHighlighted.length + 1
                );
                setMainTitleHighlighted(mainTitleHighlighted);
            }, 25);
        }, 700);

        return () => {
            clearInterval(mainInterval);
            clearInterval(secondaryInterval);
            clearTimeout(startTimeout);
            clearTimeout(intermediateTimeout);
            clearTimeout(finishingTimeout);
        };
    }, [mainTitle, secondaryTitle, onFinished]);
    return (
        <div
            className={`bri-AssistantWelcomeMessage ${
                isFinishing ? 'bri-finishing' : ''
            } ${isFinished ? 'bri-finished' : ''}`}
        >
            <div className={`bri-Headline3Font`}>
                <span className={'bri-highlighted'}>
                    {mainTitleHighlighted}
                </span>
                <span className={'bri-diffuse'}>
                    {mainTitle.slice(mainTitleHighlighted.length)}
                </span>
            </div>
            <div className={`bri-TitleFont`}>
                <span className={'bri-highlighted'}>
                    {secondaryTitleHighlighted}
                </span>
                <span className={'bri-diffuse'}>
                    {secondaryTitle.slice(secondaryTitleHighlighted.length)}
                </span>
            </div>
            <svg
                xmlns="http://www.w3.org/2000/svg"
                width="54"
                height="54"
                viewBox="0 0 54 54"
                fill="none"
                className={'bri-AssistantWelcomeMessage-logo'}
            >
                <path
                    d="M30.1713 53.8077H23.8373C22.8765 53.8077 22.093 53.0241 22.093 52.0633V31.9147H1.94453C0.983705 31.9147 0.200195 31.1312 0.200195 30.1704V23.8363C0.200195 22.8755 0.983705 22.0919 1.94453 22.0919H15.1445L5.80844 12.7558C5.12803 12.0754 5.12803 10.9702 5.80844 10.2898L10.2868 5.81142C10.6167 5.48152 11.0538 5.30006 11.5198 5.30006C11.9858 5.30006 12.4229 5.48152 12.7528 5.81142L22.0889 15.1476V1.94747C22.0889 0.986641 22.8724 0.203125 23.8332 0.203125H30.1672C31.128 0.203125 31.9115 0.986641 31.9115 1.94747V15.1476L41.2476 5.81142C41.5775 5.48152 42.0146 5.30006 42.4806 5.30006C42.9466 5.30006 43.3837 5.48152 43.7136 5.81142L48.1919 10.2898C48.5218 10.6197 48.7033 11.0568 48.7033 11.5228C48.7033 11.9888 48.5218 12.4259 48.1919 12.7558L38.8559 22.0919H52.0559C53.0167 22.0919 53.8002 22.8755 53.8002 23.8363V30.1704C53.8002 31.1312 53.0167 31.9147 52.0559 31.9147H38.8559L48.1919 41.2509C48.5218 41.5808 48.7033 42.0179 48.7033 42.4839C48.7033 42.9498 48.5218 43.387 48.1919 43.7169L43.7136 48.1952C43.3837 48.5251 42.9466 48.7066 42.4806 48.7066C42.0146 48.7066 41.5775 48.5251 41.2476 48.1952L31.9115 38.8591V52.0592C31.9115 53.02 31.128 53.8035 30.1672 53.8035L30.1713 53.8077ZM1.94453 23.3002C1.64762 23.3002 1.40844 23.5394 1.40844 23.8363V30.1704C1.40844 30.4673 1.64762 30.7064 1.94453 30.7064H22.695C23.0291 30.7064 23.3012 30.9786 23.3012 31.3126V52.0633C23.3012 52.3602 23.5404 52.5994 23.8373 52.5994H30.1713C30.4682 52.5994 30.7074 52.3602 30.7074 52.0633V37.4034C30.7074 37.1601 30.8559 36.9374 31.0785 36.8467C31.3053 36.7518 31.5651 36.8055 31.7383 36.9787L42.1053 47.3457C42.3074 47.5478 42.662 47.5478 42.8641 47.3457L47.3425 42.8674C47.4456 42.7643 47.4992 42.6323 47.4992 42.488C47.4992 42.3437 47.4414 42.2117 47.3425 42.1086L36.9754 31.7415C36.8022 31.5683 36.7487 31.3085 36.8435 31.0817C36.9383 30.8549 37.1569 30.7106 37.4002 30.7106H52.06C52.3569 30.7106 52.5961 30.4714 52.5961 30.1745V23.8404C52.5961 23.5435 52.3569 23.3043 52.06 23.3043H37.4002C37.1569 23.3043 36.9342 23.1559 36.8435 22.9332C36.7528 22.7105 36.8022 22.4466 36.9754 22.2734L47.3425 11.9063C47.5528 11.696 47.5528 11.3579 47.3425 11.1475L42.8641 6.66915C42.662 6.46709 42.3074 6.46709 42.1053 6.66915L31.7383 17.0362C31.5651 17.2094 31.3053 17.263 31.0785 17.1682C30.8517 17.0733 30.7074 16.8548 30.7074 16.6115V1.95159C30.7074 1.65468 30.4682 1.41551 30.1713 1.41551H23.8373C23.5404 1.41551 23.3012 1.65468 23.3012 1.95159V16.6115C23.3012 16.8548 23.1528 17.0775 22.9301 17.1682C22.7033 17.263 22.4435 17.2094 22.2703 17.0362L11.9033 6.66915C11.7012 6.46709 11.3466 6.46709 11.1445 6.66915L6.66617 11.1475C6.45586 11.3579 6.45586 11.696 6.66617 11.9063L17.0332 22.2734C17.2064 22.4466 17.26 22.7064 17.1651 22.9332C17.0703 23.16 16.8517 23.3043 16.6084 23.3043H1.94864L1.94453 23.3002Z"
                    fill="#FFF"
                />
                <path
                    d="M7.74806 53.8073C5.59961 53.8073 3.78104 53.0733 2.35011 51.6258C0.923306 50.1825 0.20166 48.3805 0.20166 46.265C0.20166 44.1495 0.923306 42.3474 2.35011 40.9041C3.78104 39.4567 5.59961 38.7227 7.74806 38.7227C9.89651 38.7227 11.8058 39.4608 13.2037 40.9165C14.5893 42.3598 15.2944 44.1619 15.2944 46.2691C15.2944 48.3763 14.5893 50.1784 13.2037 51.6217C11.8058 53.0774 9.97074 53.8155 7.74806 53.8155V53.8073ZM7.74806 39.9227C5.93363 39.9227 4.40785 40.5371 3.21198 41.7454C2.0161 42.9536 1.41403 44.4711 1.41403 46.2567C1.41403 48.0423 2.02022 49.5598 3.21198 50.7681C4.40785 51.9805 5.93363 52.5908 7.74806 52.5908C9.56249 52.5908 11.1707 51.9764 12.3336 50.7681C13.4965 49.5598 14.0862 48.0423 14.0862 46.2526C14.0862 44.4629 13.4965 42.9495 12.3336 41.7371C11.1707 40.5289 9.62847 39.9144 7.74806 39.9144V39.9227Z"
                    fill="#FFF"
                />
            </svg>
        </div>
    );
};

export default AssistantWelcomeMessage;
